import { useState, useRef, useEffect, useCallback } from "preact/hooks";
import type { JSX } from "preact";
import type {
  WidgetInitConfig,
  WidgetInstance,
  DisplayMessage,
} from "../types/widget";
import { useChat } from "../hooks/useChat";
import type { ViewMode } from "../hooks/useChat";
import {
  PankAgentIcon,
  DragHandleIcon,
  SendIcon,
  PaperclipIcon,
  CollapseContentIcon,
  CloseIcon,
} from "./ui/Icons";
import type { PankAgentVariant } from "./ui/Icons";
import { ChatMessage } from "./ui/ChatMessage";
import { TypingIndicator } from "./ui/TypingIndicator";

export interface ChatWidgetProps {
  config: WidgetInitConfig;
  /** Callback when widget mounts, exposing instance methods (only used in overlay mode) */
  onMount?: (instance: WidgetInstance) => void;
}

interface Position {
  x: number;
  y: number;
}

type Edge = "left" | "right";
type SnapPosition = "left" | "center" | "right";

// Dimensions
const BUBBLE_SIZE = 60;
const HANDLE_WIDTH = 32;
const CONDENSED_WIDTH = BUBBLE_SIZE + HANDLE_WIDTH;
const EXPANDED_WIDTH = 600;
const EXPANDED_HEIGHT = 64; // Matches Figma height
const FULL_MAX_WIDTH = 1280; // Max width for full mode
const FULL_HEIGHT = 600;
const REDUCED_WIDTH = 400; // Reduced mode width (right corner)
const MARGIN = 24;

type AnimatedMessage = DisplayMessage & { shouldAnimate?: boolean };

/**
 * Main chat widget component - orchestrates hooks and UI
 * Handles both overlay mode (floating bubble) and embedded mode (in container)
 */
export const ChatWidget = ({
  config,
  onMount,
}: ChatWidgetProps): JSX.Element => {
  const embedded = config.mode === "embedded";
  const placeholder = config.placeholder || "Comment puis-je vous aider ?";

  const {
    viewMode,
    messages,
    isLoading,
    open,
    close,
    sendMessage,
    setViewMode,
  } = useChat(config);

  // Expose instance methods to parent (only for overlay mode)
  useEffect(() => {
    if (onMount && !embedded) {
      onMount({ open, close });
    }
  }, [onMount, open, close, embedded]);

  // Force full mode for embedded widget
  useEffect(() => {
    if (embedded && viewMode !== "full") {
      setViewMode("full");
    }
  }, [embedded, viewMode, setViewMode]);

  const handleSend = async (message: string) => {
    if (!message.trim()) return;
    await sendMessage(message);
  };

  const handleClose = useCallback(() => {
    if (!embedded) {
      close();
    }
  }, [embedded, close]);

  const handleModeChange = useCallback(
    (mode: ViewMode) => {
      if (!embedded) {
        setViewMode(mode);
      }
    },
    [embedded, setViewMode]
  );

  // ========== UI State ==========
  const [inputValue, setInputValue] = useState("");
  // Initialize position at bottom right immediately to prevent jump from top-left
  // Not used in embedded mode
  const [position, setPosition] = useState<Position>(() => ({
    x:
      typeof window !== "undefined" && !embedded
        ? window.innerWidth - CONDENSED_WIDTH - MARGIN
        : 0,
    y:
      typeof window !== "undefined" && !embedded
        ? window.innerHeight - BUBBLE_SIZE - MARGIN
        : 0,
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [isSnapping, setIsSnapping] = useState(false); // For post-drag snap animation
  const [edge, setEdge] = useState<Edge>("right");
  const [snapPosition, setSnapPosition] = useState<SnapPosition>("center");
  const [isReduced, setIsReduced] = useState(false); // Reduced mode: smaller width, right corner
  const [isInitialized, setIsInitialized] = useState(embedded); // Embedded is immediately initialized
  const [showElements, setShowElements] = useState(embedded); // Embedded shows elements immediately
  const [isEntering, setIsEntering] = useState(!embedded); // No entrance animation for embedded

  // File attachment state
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Message Queue System
  const [displayedMessages, setDisplayedMessages] = useState<AnimatedMessage[]>(
    []
  );
  const [messageQueue, setMessageQueue] = useState<DisplayMessage[]>([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const processedMessageIds = useRef<Set<string>>(new Set());
  const isFirstLoad = useRef(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragStart = useRef<Position>({ x: 0, y: 0 });
  const initialPos = useRef<Position>({ x: 0, y: 0 });
  const prevMode = useRef<ViewMode>(viewMode);

  // Sync messages with queue system
  useEffect(() => {
    // Get the set of current message IDs from props
    const currentMessageIds = new Set(messages.map((m) => m.id));

    // Clean up processedMessageIds - remove IDs that no longer exist
    processedMessageIds.current.forEach((id) => {
      if (!currentMessageIds.has(id)) {
        processedMessageIds.current.delete(id);
      }
    });

    // 1. Update displayed messages: sync content, isStreaming, and remove stale messages
    setDisplayedMessages((prev) => {
      // Update existing messages and remove any that no longer exist in messages
      return prev
        .filter((msg) => currentMessageIds.has(msg.id))
        .map((msg) => {
          const source = messages.find((m) => m.id === msg.id);
          if (source) {
            // Update content and isStreaming from source
            return { ...source, shouldAnimate: msg.shouldAnimate };
          }
          return msg;
        });
    });

    // Also clean up message queue - remove messages that no longer exist
    setMessageQueue((prev) =>
      prev.filter((msg) => currentMessageIds.has(msg.id))
    );

    // 2. Identify new messages (not in processedMessageIds)
    const newMessages = messages.filter(
      (m) => !processedMessageIds.current.has(m.id)
    );

    if (newMessages.length > 0) {
      newMessages.forEach((m) => processedMessageIds.current.add(m.id));

      // If it's the very first load of messages (history), show them immediately
      if (isFirstLoad.current && newMessages.length > 0) {
        setDisplayedMessages((prev) => [
          ...prev,
          ...newMessages.map((m) => ({ ...m, shouldAnimate: false })),
        ]);
        isFirstLoad.current = false;
      } else {
        // If we are in full mode, queue them for animation
        if (viewMode === "full") {
          setMessageQueue((prev) => [...prev, ...newMessages]);
        } else {
          // If not in full mode, just add them without animation
          setDisplayedMessages((prev) => [
            ...prev,
            ...newMessages.map((m) => ({ ...m, shouldAnimate: false })),
          ]);
        }
      }
    }
  }, [messages, viewMode]);

  // Process Queue
  useEffect(() => {
    if (messageQueue.length > 0 && !isProcessingQueue) {
      const nextMsg = messageQueue[0];
      if (!nextMsg) return;

      setIsProcessingQueue(true);

      setDisplayedMessages((prev) => [
        ...prev,
        { ...nextMsg, shouldAnimate: true },
      ]);

      setMessageQueue((prev) => prev.slice(1));

      // Wait for animation to finish before processing next
      setTimeout(() => {
        setIsProcessingQueue(false);
      }, 600); // Match CSS animation duration
    }
  }, [messageQueue, isProcessingQueue]);

  // Calculate positions for different modes
  const getCondensedPosition = useCallback(
    (targetEdge: Edge = edge): Position => ({
      x:
        targetEdge === "right"
          ? window.innerWidth - CONDENSED_WIDTH - MARGIN
          : MARGIN,
      y: window.innerHeight - BUBBLE_SIZE - MARGIN,
    }),
    [edge]
  );

  const getExpandedPosition = useCallback(
    (snap: SnapPosition = snapPosition): Position => {
      const y = window.innerHeight - EXPANDED_HEIGHT - MARGIN;
      let x = 0;
      switch (snap) {
        case "left":
          x = MARGIN;
          break;
        case "center":
          x =
            (window.innerWidth -
              Math.min(EXPANDED_WIDTH, window.innerWidth - 48)) /
            2;
          break;
        case "right":
          x = window.innerWidth - EXPANDED_WIDTH - MARGIN;
          break;
      }
      return { x, y };
    },
    [snapPosition]
  );

  // Calculate full mode width (80% of viewport, max 1280px)
  const getFullModeWidth = useCallback(() => {
    return Math.min(
      window.innerWidth * 0.8,
      FULL_MAX_WIDTH,
      window.innerWidth - 48
    );
  }, []);

  // Calculate full mode height (80% of viewport)
  const getFullModeHeight = useCallback((reduced: boolean) => {
    if (reduced) {
      return Math.min(FULL_HEIGHT, window.innerHeight - 48);
    }
    return Math.min(window.innerHeight * 0.8, window.innerHeight - 48);
  }, []);

  const getFullPosition = useCallback(
    (reduced: boolean = isReduced): Position => {
      if (reduced) {
        // Reduced mode: bottom right corner
        const reducedHeight = getFullModeHeight(true);
        return {
          x:
            window.innerWidth -
            Math.min(REDUCED_WIDTH, window.innerWidth - 48) -
            MARGIN,
          y: window.innerHeight - reducedHeight - MARGIN,
        };
      }
      // Normal full mode: bottom center with 80% dimensions
      const fullWidth = getFullModeWidth();
      const fullHeight = getFullModeHeight(false);
      return {
        x: Math.max(MARGIN, (window.innerWidth - fullWidth) / 2),
        y: window.innerHeight - fullHeight - MARGIN,
      };
    },
    [isReduced, getFullModeWidth, getFullModeHeight]
  );

  // Initialize position immediately at the correct spot (no jump)
  useEffect(() => {
    if (!isInitialized) {
      // Set position immediately
      setPosition({
        x: window.innerWidth - CONDENSED_WIDTH - MARGIN,
        y: window.innerHeight - BUBBLE_SIZE - MARGIN,
      });
      setIsInitialized(true);
      // Remove entering state after animation completes
      setTimeout(() => setIsEntering(false), 400);
    }
  }, [isInitialized]);

  // Handle mode transitions - CSS handles the animation, we just update position
  useEffect(() => {
    if (prevMode.current !== viewMode && isInitialized) {
      // Hide elements first for staggered reveal
      setShowElements(false);

      // Reset reduced mode when entering full mode from another mode
      if (viewMode === "full" && prevMode.current !== "full") {
        setIsReduced(false);
      }

      let targetPos: Position;
      switch (viewMode) {
        case "condensed":
          targetPos = getCondensedPosition();
          break;
        case "expanded":
          setSnapPosition("center");
          targetPos = getExpandedPosition("center");
          break;
        case "full":
          targetPos = getFullPosition(false); // Always start full mode non-reduced
          break;
      }

      setPosition(targetPos);

      // Stagger element appearance - CSS transitions handle the actual animation
      // We just need to trigger the elements-visible class at the right time
      const elementDelay = viewMode === "full" ? 200 : 100;
      setTimeout(() => {
        setShowElements(true);
        if (viewMode === "expanded" || viewMode === "full") {
          inputRef.current?.focus();
        }
      }, elementDelay);
    }
    prevMode.current = viewMode;
  }, [
    viewMode,
    isInitialized,
    getCondensedPosition,
    getExpandedPosition,
    getFullPosition,
  ]);

  // Initial showElements state
  useEffect(() => {
    if (isInitialized && viewMode === "condensed") {
      setShowElements(true);
    }
  }, [isInitialized, viewMode]);

  // Handle window resize - CSS transitions handle animation automatically
  useEffect(() => {
    const handleResize = () => {
      if (!isDragging && isInitialized) {
        switch (viewMode) {
          case "condensed":
            setPosition(getCondensedPosition());
            break;
          case "expanded":
            setPosition(getExpandedPosition());
            break;
          case "full":
            setPosition(getFullPosition(isReduced));
            break;
        }
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    isDragging,
    isInitialized,
    viewMode,
    isReduced,
    getCondensedPosition,
    getExpandedPosition,
    getFullPosition,
  ]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (viewMode === "full") {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [displayedMessages, viewMode]);

  // Prevent body scroll when scrolling inside messages container
  useEffect(() => {
    if (viewMode !== "full") return;

    const container = containerRef.current;
    if (!container) return;

    const messagesContainer = container.querySelector(
      ".pankagent-unified-messages"
    ) as HTMLElement;
    if (!messagesContainer) return;

    const preventBodyScroll = (e: WheelEvent) => {
      // Stop propagation to prevent body from scrolling
      e.stopPropagation();
    };

    messagesContainer.addEventListener("wheel", preventBodyScroll, {
      passive: true,
    });

    return () => {
      messagesContainer.removeEventListener("wheel", preventBodyScroll);
    };
  }, [viewMode]);

  // Drag handlers
  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setIsSnapping(false);
      dragStart.current = { x: e.clientX, y: e.clientY };
      initialPos.current = { ...position };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [position]
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!isDragging) return;
      const deltaX = e.clientX - dragStart.current.x;
      const deltaY = e.clientY - dragStart.current.y;
      setPosition({
        x: initialPos.current.x + deltaX,
        y: initialPos.current.y + deltaY,
      });
    },
    [isDragging]
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      if (!isDragging) return;
      setIsDragging(false);
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);

      // Enable snapping animation for post-drag snap
      setIsSnapping(true);

      if (viewMode === "condensed") {
        // Snap to edge
        const centerX = window.innerWidth / 2;
        const targetEdge: Edge = position.x < centerX ? "left" : "right";
        setEdge(targetEdge);

        const maxY = window.innerHeight - BUBBLE_SIZE - MARGIN;
        const targetY = Math.min(Math.max(MARGIN, position.y), maxY);
        const targetX =
          targetEdge === "left"
            ? MARGIN
            : window.innerWidth - CONDENSED_WIDTH - MARGIN;

        setPosition({ x: targetX, y: targetY });
      } else if (viewMode === "expanded") {
        // Snap to left/center/right
        const centerX = window.innerWidth / 2;
        const currentCenterX = position.x + EXPANDED_WIDTH / 2;

        let snap: SnapPosition = "center";
        if (currentCenterX < centerX - window.innerWidth / 6) {
          snap = "left";
        } else if (currentCenterX > centerX + window.innerWidth / 6) {
          snap = "right";
        }

        setSnapPosition(snap);
        setPosition(getExpandedPosition(snap));
      } else if (viewMode === "full") {
        // Keep in bounds - use correct width/height based on reduced mode
        const currentWidth = isReduced ? REDUCED_WIDTH : getFullModeWidth();
        const currentHeight = getFullModeHeight(isReduced);
        const maxX = window.innerWidth - currentWidth - MARGIN;
        const maxY = window.innerHeight - currentHeight - MARGIN;
        setPosition({
          x: Math.min(Math.max(MARGIN, position.x), Math.max(MARGIN, maxX)),
          y: Math.min(Math.max(MARGIN, position.y), Math.max(MARGIN, maxY)),
        });
      }

      // Clear snapping state after animation completes
      setTimeout(() => setIsSnapping(false), 500);
    },
    [isDragging, viewMode, position, isReduced, getExpandedPosition]
  );

  // Input handlers
  const handleSendClick = useCallback(() => {
    if (inputValue.trim()) {
      handleSend(inputValue);
      setInputValue("");
    }
  }, [inputValue, handleSend]);

  // File attachment handlers
  const handleFileSelect = useCallback((e: Event) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      setAttachedFile(file);
      console.log("File attached:", file.name, file.size, file.type);
    }
    // Reset input so the same file can be selected again
    input.value = "";
  }, []);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback(() => {
    setAttachedFile(null);
    console.log("File removed");
  }, []);

  // Check if user has sent at least one message (conversation started)
  const hasUserMessages = messages.some((m) => m.role === "user");

  // Randomly choose a color variant (persisted for widget session using ref)
  const activeVariantRef = useRef<PankAgentVariant>(
    Math.random() < 0.5 ? "green" : "pink"
  );

  // Use the active variant only when user has sent messages
  const logoVariant: PankAgentVariant = hasUserMessages
    ? activeVariantRef.current
    : "default";

  const handleBubbleClick = useCallback(() => {
    if (!isDragging) {
      if (viewMode === "condensed") {
        // If conversation has started, go directly to full mode
        // Otherwise, go to expanded mode for first message
        handleModeChange(hasUserMessages ? "full" : "expanded");
      }
    }
  }, [isDragging, viewMode, hasUserMessages, handleModeChange]);

  const handleLogoClick = useCallback(() => {
    if (viewMode === "expanded") {
      // In expanded mode, clicking logo goes back to condensed
      handleModeChange("condensed");
    }
    // In full mode, logo is not clickable (no action)
  }, [viewMode, handleModeChange]);

  const getHeight = () => {
    if (embedded) return "100%";
    switch (viewMode) {
      case "condensed":
        return BUBBLE_SIZE;
      case "expanded":
        return EXPANDED_HEIGHT;
      case "full":
        return getFullModeHeight(isReduced);
    }
  };

  const getWidth = () => {
    if (embedded) return "100%";
    switch (viewMode) {
      case "condensed":
        return CONDENSED_WIDTH;
      case "expanded":
        return Math.min(EXPANDED_WIDTH, window.innerWidth - 48);
      case "full":
        return isReduced
          ? Math.min(REDUCED_WIDTH, window.innerWidth - 48)
          : getFullModeWidth();
    }
  };

  const showHandle =
    !embedded && (viewMode === "condensed" || viewMode === "expanded");
  const showMessages = viewMode === "full";
  const showHeader = viewMode === "full" && !embedded; // No header in embedded mode

  // Toggle reduced mode (smaller width, right corner vs full centered)
  const handleToggleReduced = useCallback(() => {
    if (embedded) return; // No reduced mode in embedded
    const newReduced = !isReduced;
    setIsReduced(newReduced);
    setPosition(getFullPosition(newReduced));
  }, [isReduced, getFullPosition, embedded]);

  // Ref for textarea in full mode
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Build style object - embedded mode uses relative positioning
  const widgetStyle = embedded
    ? {
        width: "100%",
        height: "100%",
      }
    : {
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${getWidth()}px`,
        height: `${getHeight()}px`,
      };

  // Container class based on mode
  const containerClass = embedded
    ? "pankagent-embedded-container"
    : "pankagent-container";

  return (
    <div class={containerClass}>
      <div
        ref={containerRef}
        class={`pankagent-unified-widget mode-${viewMode} ${
          isDragging ? "dragging" : ""
        } ${isSnapping ? "snapping" : ""} ${
          viewMode === "condensed"
            ? edge === "right"
              ? "edge-right"
              : "edge-left"
            : ""
        } ${viewMode === "full" && isReduced ? "reduced" : ""} ${
          showElements ? "elements-visible" : ""
        } ${isEntering ? "entering" : ""} ${embedded ? "embedded" : ""}`}
        style={widgetStyle}
      >
        {/* Header - Only in full mode and not embedded - positioned top right */}
        {showHeader && (
          <div class="pankagent-unified-header">
            <div class="pankagent-unified-header-actions">
              <button
                class="pankagent-unified-header-btn"
                onClick={handleToggleReduced}
                aria-label={isReduced ? "Agrandir" : "Réduire"}
              >
                <CollapseContentIcon size={32} />
              </button>
              <div
                class="pankagent-unified-header-btn drag-handle"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
              >
                <DragHandleIcon size={32} />
              </div>
            </div>
          </div>
        )}

        {/* Messages - Only in full mode */}
        {showMessages && (
          <div class="pankagent-unified-messages">
            {displayedMessages.map((msg, index) => (
              <ChatMessage
                key={msg.id}
                role={msg.role}
                content={msg.content}
                isStreaming={msg.isStreaming}
                index={index}
                isNew={msg.shouldAnimate}
                variant={logoVariant}
              />
            ))}
            {/* Show typing indicator when loading and no streaming message is present in displayed or queued messages */}
            {isLoading &&
              !displayedMessages.some((m) => m.isStreaming) &&
              !messageQueue.some((m) => m.isStreaming) && (
                <div
                  class="pankagent-message assistant typing-indicator-message"
                  style="--msg-index: 0"
                >
                  <div class="pankagent-message-avatar">
                    <PankAgentIcon size={56} variant={logoVariant} />
                  </div>
                  <div class="pankagent-message-content">
                    <TypingIndicator />
                  </div>
                </div>
              )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Input Area - Main interactive zone */}
        <div class="pankagent-unified-input-area">
          {/* Logo/Bubble - Only show in condensed and expanded modes */}
          {viewMode !== "full" && (
            <button
              class="pankagent-unified-logo"
              onClick={
                viewMode === "condensed" ? handleBubbleClick : handleLogoClick
              }
              aria-label={
                viewMode === "condensed" ? "Ouvrir le chat" : "Fermer"
              }
            >
              <PankAgentIcon
                size={viewMode === "condensed" ? 48 : 48}
                variant={logoVariant}
              />
            </button>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            class="pankagent-file-input"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          {/* Expanded mode: special layout matching Figma */}
          {viewMode === "expanded" && (
            <div class="pankagent-expanded-input-group">
              {/* Attach button in its own container */}
              <div class="pankagent-attach-container">
                <button
                  class="pankagent-attach-btn"
                  aria-label="Joindre un fichier"
                  onClick={handleAttachClick}
                >
                  <PaperclipIcon size={16.5} />
                  {attachedFile && <span class="pankagent-attach-badge" />}
                </button>
              </div>

              {/* Input field container with send button */}
              <div class="pankagent-input-container">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onInput={(e) => setInputValue(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (inputValue.trim()) {
                        handleSend(inputValue);
                        setInputValue("");
                      }
                    }
                    if (e.key === "Escape" && !embedded) {
                      handleModeChange("condensed");
                    }
                  }}
                  placeholder={placeholder}
                />
                <button
                  class={`pankagent-send-btn ${
                    inputValue.trim() ? "active" : ""
                  }`}
                  onClick={handleSendClick}
                  disabled={!inputValue.trim()}
                  aria-label="Envoyer"
                >
                  <SendIcon size={33} />
                </button>
              </div>
            </div>
          )}

          {/* Full mode: new input layout matching Figma */}
          {viewMode === "full" && (
            <div class="pankagent-full-input-container">
              <textarea
                ref={textareaRef}
                class="pankagent-full-input-textarea"
                value={inputValue}
                onInput={(e) => setInputValue(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendClick();
                  }
                  if (e.key === "Escape" && !embedded) {
                    handleModeChange("condensed");
                  }
                }}
                placeholder="Écrivez votre réponse ici..."
              />
              <div class="pankagent-full-input-actions">
                <div class="pankagent-full-input-left">
                  {/* Logo button only shown in overlay mode */}
                  {!embedded && (
                    <button
                      class="pankagent-full-logo"
                      onClick={() => handleModeChange("condensed")}
                      aria-label="Fermer le chat"
                    >
                      <PankAgentIcon size={40} variant={logoVariant} />
                    </button>
                  )}
                  <button
                    class="pankagent-full-attach-btn"
                    aria-label="Joindre un fichier"
                    onClick={handleAttachClick}
                  >
                    <PaperclipIcon size={16} />
                  </button>
                  {attachedFile && (
                    <div class="pankagent-file-info">
                      <span class="pankagent-file-name">
                        {attachedFile.name}
                      </span>
                      <button
                        class="pankagent-file-remove"
                        onClick={handleRemoveFile}
                        aria-label="Retirer le fichier"
                      >
                        <CloseIcon size={14} />
                      </button>
                    </div>
                  )}
                </div>
                <button
                  class="pankagent-full-send-btn"
                  onClick={handleSendClick}
                  disabled={!inputValue.trim()}
                  aria-label="Envoyer"
                >
                  <SendIcon size={33} />
                </button>
              </div>
            </div>
          )}

          {/* Drag Handle */}
          {showHandle && (
            <div
              class="pankagent-unified-handle"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            >
              <DragHandleIcon size={viewMode === "expanded" ? 37 : 24} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
